// Datos de la ficha del SKU: libro y piezas del catálogo, historial de precios y ventas del wizard.
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ApiError } from '@/api/errors';
import {
    inventoryLensApi,
    type InventoryLensPeriod,
} from '@/api/services/retail-catalog/backoffice';
import type { BackofficeSalesFilters } from '@/api/services/retail-wizard/contract';
import { backofficeSalesApi } from '@/api/services/retail-wizard/retail-wizard.client';
import { INVENTORY_QUERY_KEY } from './useInventarioLens';

const LEDGER_PAGE_SIZE = 20;
const SALES_PAGE_SIZE = 100;

/** Un 4xx no se arregla reintentando: se muestra el motivo de una vez. */
export function retryUnlessClientError(failureCount: number, error: unknown): boolean {
    if (error instanceof ApiError && error.httpStatus < 500) return false;
    return failureCount < 2;
}

/** Página siguiente; un token repetido no avanza el libro y corta la paginación. */
export function nextLedgerToken(
    last: { nextToken: string | null },
    lastToken: string | undefined,
): string | undefined {
    if (!last.nextToken || last.nextToken === lastToken) return undefined;
    return last.nextToken;
}

/** Libro paginado del SKU; cada página trae piezas, apartadas y precio vigente. */
export function useInventorySku(
    sku: string | null,
    period: InventoryLensPeriod,
    storeId: string | undefined,
    includeHolds = false,
) {
    return useInfiniteQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'sku', sku, period, storeId ?? null, includeHolds],
        enabled: sku !== null,
        initialPageParam: undefined as string | undefined,
        queryFn: ({ pageParam }) =>
            inventoryLensApi.getSku(sku ?? '', {
                period,
                storeId,
                limit: LEDGER_PAGE_SIZE,
                nextToken: pageParam,
                includeHolds,
            }),
        getNextPageParam: (last, _pages, lastToken) => nextLedgerToken(last, lastToken),
        retry: retryUnlessClientError,
        staleTime: 60_000,
    });
}

/** Historial de precios; solo se pide al abrirlo. */
export function useSkuPrices(sku: string | null, enabled: boolean) {
    return useQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'prices', sku],
        queryFn: () => inventoryLensApi.listSkuPrices(sku ?? ''),
        enabled: enabled && sku !== null,
        retry: retryUnlessClientError,
        staleTime: 60_000,
    });
}

/** Ventas del SKU en el periodo, de `GET /backoffice/sales` del wizard; traen PII y solo se piden habilitadas. */
export function useSkuSales(filters: BackofficeSalesFilters | null, enabled: boolean) {
    return useInfiniteQuery({
        queryKey: [...INVENTORY_QUERY_KEY, 'sales', filters],
        enabled: enabled && filters !== null && filters.retailerId !== '',
        initialPageParam: undefined as string | undefined,
        queryFn: ({ pageParam }) =>
            backofficeSalesApi.list(filters as BackofficeSalesFilters, pageParam, SALES_PAGE_SIZE),
        getNextPageParam: (last) => last.nextToken ?? undefined,
        retry: retryUnlessClientError,
        staleTime: 60_000,
    });
}
